<script lang="ts" module>
  import type { SelectOption } from './Select.svelte';

  // Consecutive options sharing a `group` render under its header; the order is the caller's.
  export type ComboboxOption = SelectOption & { group?: string };
</script>

<script lang="ts">
  // An editable Select (ARIA 1.2 combobox, list autocomplete): pick an option or keep typed free text.
  // Free text commits on Enter/blur; text equal to an option's label picks that option instead.

  interface Props {
    label: string;
    options: ComboboxOption[];
    /** What the field shows while not being edited: the picked option's label or the free text. */
    text: string;
    onPick: (value: string) => void;
    /** Trimmed free text; '' when the field was cleared. */
    onText: (text: string) => void;
    placeholder?: string;
    maxlength?: number;
    disabled?: boolean;
    'aria-describedby'?: string;
    class?: string;
  }

  let {
    label,
    options,
    text,
    onPick,
    onText,
    placeholder,
    maxlength,
    disabled = false,
    'aria-describedby': ariaDescribedBy,
    class: klass = '',
  }: Props = $props();

  const uid = `vd-combobox-${Math.random().toString(36).slice(2, 9)}`;
  const optionId = (i: number): string => `${uid}-opt-${i}`;

  let term = $derived(text);
  let open = $state(false);
  let dirty = $state(false);
  let highlight = $state(-1);

  const matches = $derived.by(() => {
    const needle = term.trim().toLowerCase();
    return dirty && needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  });

  // Built from the matches, so a filtered-out group loses its header too.
  const sections = $derived.by(() => {
    const out: { group?: string; items: { option: ComboboxOption; i: number }[] }[] = [];
    matches.forEach((option, i) => {
      const last = out.at(-1);
      if (last && last.group === option.group) last.items.push({ option, i });
      else out.push({ group: option.group, items: [{ option, i }] });
    });
    return out;
  });

  function settle(): void {
    open = false;
    dirty = false;
    highlight = -1;
    term = text;
  }

  function pick(option: ComboboxOption): void {
    onPick(option.value);
    settle();
  }

  function commit(): void {
    const typed = term.trim();
    const exact = options.find((o) => o.label.toLowerCase() === typed.toLowerCase());
    if (exact) return pick(exact);
    onText(typed);
    settle();
  }

  function onkeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      open = true;
      if (matches.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      highlight = highlight < 0 && step < 0 ? matches.length - 1 : (highlight + step + matches.length) % matches.length;
      document.getElementById(optionId(highlight))?.scrollIntoView?.({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && highlight >= 0 && matches[highlight]) pick(matches[highlight]);
      else commit();
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      settle();
    }
  }
</script>

{#snippet item(option: ComboboxOption, i: number)}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <li id={optionId(i)} class="vd-combobox__opt" class:hi={i === highlight} role="option" aria-selected={i === highlight} onclick={() => pick(option)}>
    {option.label}
  </li>
{/snippet}

<div class="vd-combobox {klass}">
  <label class="vd-combobox__label" for={uid}>{label}</label>
  <div class="vd-combobox__box">
    <input
      id={uid}
      class="vd-combobox__el"
      type="text"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      aria-expanded={open}
      aria-controls={`${uid}-list`}
      aria-activedescendant={open && highlight >= 0 ? optionId(highlight) : undefined}
      aria-describedby={ariaDescribedBy}
      {placeholder}
      {maxlength}
      {disabled}
      bind:value={term}
      oninput={() => {
        dirty = true;
        open = true;
        highlight = -1;
      }}
      onclick={() => (open = true)}
      onblur={() => (dirty ? commit() : settle())}
      {onkeydown}
    />
    <span class="vd-combobox__chevron" aria-hidden="true">▾</span>
    <!-- Focus stays in the input (aria-activedescendant), hence the swallowed mousedown. -->
    <ul
      id={`${uid}-list`}
      class="vd-combobox__list"
      role="listbox"
      aria-label={label}
      hidden={!open || matches.length === 0}
      onmousedown={(e) => e.preventDefault()}
    >
      {#each sections as section, s (s)}
        {#if section.group}
          <li class="vd-combobox__group" role="group" aria-labelledby={`${uid}-grp-${s}`}>
            <div id={`${uid}-grp-${s}`} class="vd-combobox__group-label">{section.group}</div>
            <ul class="vd-combobox__group-list" role="presentation">
              {#each section.items as { option, i } (option.value)}{@render item(option, i)}{/each}
            </ul>
          </li>
        {:else}
          {#each section.items as { option, i } (option.value)}{@render item(option, i)}{/each}
        {/if}
      {/each}
    </ul>
  </div>
</div>

<style lang="scss">
  // Select's labelled-field metrics and tokens, so the two sit interchangeably in one form row.
  .vd-combobox {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    max-width: 100%;
    min-width: 0;
  }

  .vd-combobox__label {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-mute);
  }

  .vd-combobox__box {
    position: relative;
    display: flex;
    align-items: center;
  }

  .vd-combobox__el {
    flex: 1;
    min-width: 0;
    max-width: 100%;
    padding: var(--field-pad-y) 2rem var(--field-pad-y) var(--field-pad-x);
    font-family: var(--font-ui);
    font-size: var(--text-field);
    line-height: 1.5;
    border: 1px solid var(--line);
    border-radius: var(--field-radius);
    background: var(--paper);
    color: var(--ink);
    text-overflow: ellipsis;
    transition:
      border-color 0.2s ease,
      background 0.2s ease;

    &::placeholder {
      color: var(--ink-mute);
    }

    &:hover:not(:disabled) {
      border-color: color-mix(in srgb, var(--accent) 45%, var(--line));
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  .vd-combobox__chevron {
    position: absolute;
    right: 10px;
    font-size: 0.7rem;
    color: var(--ink-mute);
    pointer-events: none;
  }

  .vd-combobox__list {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    max-height: 16rem;
    margin: 0;
    padding: 0.25rem 0;
    overflow-y: auto;
    list-style: none;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--paper);
  }

  .vd-combobox__group-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .vd-combobox__group-label {
    padding: 0.6rem 0.75rem 0.25rem;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-mute);
  }

  .vd-combobox__opt {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 0.35rem 0.75rem;
    font-family: var(--font-ui);
    font-size: var(--text-body);
    color: var(--ink);
    cursor: pointer;

    &:hover,
    &.hi {
      background: color-mix(in srgb, var(--accent) 12%, var(--paper));
    }
  }
</style>
